/*
 * Copyright Mimic Networks, Inc. 2026.
 */

import { Divider, Tag } from 'antd';

import { Node } from '@/client';
import { useMimicTranslation } from '@/hooks/useMimicTranslation';
import { Text } from '@/primitives/Text';
import { Title } from '@/primitives/Title';

import { destinationDisplayNames, DestinationType, monoTagStyle, tagStyle } from './constants';
import { CreateAlertWizardFooter } from './CreateAlertWizardFooter';

type ReviewStepProps = {
  destinationType: DestinationType;
  name: string;
  description: string;
  webhookUrl: string;
  eventTypes: string[];
  selectedNodes: Node[];
  selectedTags: string[];
  includeFilters: Record<string, string>;
  excludeFilters: Record<string, string>;
  onPrevious: () => void;
  onSubmit: () => void;
  loading: boolean;
  isEditMode?: boolean;
};

export function ReviewStep({
  destinationType,
  name,
  description,
  webhookUrl,
  eventTypes,
  selectedNodes,
  selectedTags,
  includeFilters,
  excludeFilters,
  onPrevious,
  onSubmit,
  loading,
  isEditMode,
}: ReviewStepProps) {
  const { t } = useMimicTranslation('notificationManagement');
  const displayName = destinationDisplayNames[destinationType];

  return (
    <>
      <div className="create-alert-wizard-content">
        <div style={{ maxWidth: 640, margin: '0 auto', padding: '32px 0' }}>
          <Title level={3}>{t('review.title')}</Title>
          <Text type="subtle" size="md">
            {t('review.subtitle')}
          </Text>

          <div style={{ marginTop: '32px', display: 'flex', flexDirection: 'column', gap: '24px' }}>
            <ReviewField label={t('review.destination')} value={displayName} />
            <ReviewField label={t('review.connectionName')} value={name} />
            {description && <ReviewField label={t('review.description')} value={description} />}
            <ReviewField label={t('review.webhookUrl')} value={webhookUrl} />

            <Divider style={{ margin: 0 }} />

            <div>
              <Text strong size="md">
                {t('review.eventTypes')}
              </Text>
              <div style={{ marginTop: '8px', display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
                {eventTypes.map((type) => (
                  <Tag key={type} style={tagStyle}>
                    {t(`parameters.events.${type}` as Parameters<typeof t>[0])}
                  </Tag>
                ))}
                {eventTypes.length === 0 && <Text type="subtle">{t('review.none')}</Text>}
              </div>
            </div>

            <div>
              <Text strong size="md">
                {t('review.nodes')}
              </Text>
              <div style={{ marginTop: '8px', display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
                {selectedNodes.map((node) => (
                  <Tag key={node.id} style={monoTagStyle}>
                    {node.name}
                  </Tag>
                ))}
                {selectedNodes.length === 0 && <Text type="subtle">{t('review.allNodes')}</Text>}
              </div>
            </div>

            {selectedTags.length > 0 && (
              <div>
                <Text strong size="md">
                  {t('review.tags')}
                </Text>
                <div style={{ marginTop: '8px', display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
                  {selectedTags.map((tag) => (
                    <Tag key={tag} style={tagStyle}>
                      {tag}
                    </Tag>
                  ))}
                </div>
              </div>
            )}

            {Object.keys(includeFilters).length > 0 && (
              <div>
                <Text strong size="md">
                  {t('review.includeFilters')}
                </Text>
                <div style={{ marginTop: '8px', display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
                  {Object.entries(includeFilters).map(([key, value]) => (
                    <Tag key={key} style={monoTagStyle}>
                      {key}: {value}
                    </Tag>
                  ))}
                </div>
              </div>
            )}

            {Object.keys(excludeFilters).length > 0 && (
              <div>
                <Text strong size="md">
                  {t('review.excludeFilters')}
                </Text>
                <div style={{ marginTop: '8px', display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
                  {Object.entries(excludeFilters).map(([key, value]) => (
                    <Tag key={key} style={monoTagStyle}>
                      {key}: {value}
                    </Tag>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      <CreateAlertWizardFooter
        onNext={onSubmit}
        onPrevious={onPrevious}
        nextLabel={isEditMode ? t('review.update') : t('review.submit')}
        previousLabel={t('review.previousLabel')}
        loading={loading}
      />
    </>
  );
}

function ReviewField({ label, value }: { label: string; value: string }) {
  return (
    <div>
      <Text strong size="md">
        {label}
      </Text>
      <br />
      <Text size="md">{value}</Text>
    </div>
  );
}
